/**
 * KPI Subscription Context
 *
 * Centralizes real-time KPI subscription management. Owns a single subscription
 * to KPI variant update/delete and root KPI update events — either global (KPI Library view)
 * or page-scoped with server-side filtering (Page view). Performs cache invalidation
 * and forwards event data to registered listeners.
 *
 * Consumers register callbacks via `registerListener` to receive typed event data
 * without needing to manage their own subscriptions. Page-scoped mode is activated
 * via `setPageIds`, which tears down the global subscription and reconnects with
 * page_ids variables for server-side deckline filtering.
 */

import useApiClients from '@hooks/useApiClients.tsx';
import { createId } from '@paralleldrive/cuid2';
import {
  OnDeleteKPIVariantSubscription,
  OnUpdateKPIVariantSubscription,
  OnUpdateRootKPISubscription,
} from '@services/api/deckManagement/graphql/schema/API';
import {
  onDeleteKPIVariant,
  onUpdateKPIVariant,
  onUpdateRootKPI,
} from '@services/api/deckManagement/graphql/schema/subscriptions';
import { kpiVariantQueryKeys } from '@services/api/deckManagement/hooks/useKPIVariants';
import { rootKPIQueryKeys } from '@services/api/deckManagement/hooks/useRootKPIs';
import { isValidSubscriptionData } from '@services/api/deckManagement/hooks/useSubscriptionFactory';
import { useQueryClient } from '@tanstack/react-query';
import { isEqual } from 'lodash';
import { createContext, ReactNode, use, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  KPISubscriptionContextType,
  KPISubscriptionListenerCallbacks,
  KPISubscriptionProviderProps,
} from 'types/KPISubscriptionContext.types';

export const KPISubscriptionContext = createContext<KPISubscriptionContextType>({
  registerListener: () => () => {},
});

export const KPISubscriptionProvider = ({ children }: KPISubscriptionProviderProps): ReactNode => {
  const queryClient = useQueryClient();
  const { deckManagementSubscriptionClient } = useApiClients();

  // Derived page IDs — computed from registered listeners
  const [pageIds, setPageIds] = useState<string[] | null>(null);

  // Stable refs for subscription handlers
  const queryClientRef = useRef(queryClient);
  const subscriptionClientRef = useRef(deckManagementSubscriptionClient);
  queryClientRef.current = queryClient;
  subscriptionClientRef.current = deckManagementSubscriptionClient;

  // Listener registry — stores callbacks and optional page scope per listener
  const listenersRef = useRef(new Map<string, { callbacks: KPISubscriptionListenerCallbacks; pageIds?: string[] }>());

  /**
   * Recomputes the active page IDs from all registered listeners.
   * If any listener omits pageIds (needs global), returns null (global mode).
   * Otherwise, returns the union of all listeners' pageIds.
   * Only updates state if the computed value differs from current.
   */
  const recomputeActivePageIds = useCallback(() => {
    const listeners = Array.from(listenersRef.current.values());
    let newPageIds: string[] | null;

    if (listeners.length === 0 || listeners.some((l) => !l.pageIds)) {
      newPageIds = null;
    } else {
      const allPageIds = [...new Set(listeners.flatMap((l) => l.pageIds ?? []))];
      newPageIds = allPageIds.length > 0 ? allPageIds : null;
    }

    setPageIds((prev) => (isEqual(prev, newPageIds) ? prev : newPageIds));
  }, []);

  /**
   * Register a listener for KPI subscription events.
   * Optionally scope to specific page IDs for server-side filtering.
   * Returns an unregister function for cleanup.
   */
  const registerListener = useCallback(
    (callbacks: KPISubscriptionListenerCallbacks, options?: { pageIds?: string[] }): (() => void) => {
      const id = createId();
      listenersRef.current.set(id, { callbacks, pageIds: options?.pageIds });
      recomputeActivePageIds();
      return () => {
        listenersRef.current.delete(id);
        recomputeActivePageIds();
      };
    },
    [recomputeActivePageIds],
  );

  // Single subscription — global or page-scoped based on pageIds state.
  // Note: When page_ids is provided, the server only delivers events for mutations that affect
  // page-specific data (variant_overrides, root_kpi changes). Non-cascading changes like
  // variant_name edits will not trigger an event in page-scoped mode.
  useEffect(() => {
    const client = subscriptionClientRef.current;
    const variables = pageIds ? { page_ids: pageIds } : undefined;
    console.debug('[KPISubscription] Establishing subscriptions:', {
      mode: pageIds ? 'page-scoped' : 'global',
      pageIds,
      events: ['onUpdateKPIVariant', 'onDeleteKPIVariant', 'onUpdateRootKPI'],
    });

    const updateVariantSub = client.graphql({ query: onUpdateKPIVariant, ...(variables && { variables }) }).subscribe({
      next: ({ data }) => {
        if (isValidSubscriptionData(data, 'onUpdateKPIVariant')) {
          console.debug('[KPISubscription] onUpdateKPIVariant data received:', data);
          // Cache invalidation
          queryClientRef.current.invalidateQueries({ queryKey: kpiVariantQueryKeys.all });
          // Notify listeners
          listenersRef.current.forEach(({ callbacks }) =>
            callbacks.onUpdateKPIVariant?.(data as OnUpdateKPIVariantSubscription),
          );
        }
      },
      error: (error) => console.error('[KPISubscription] onUpdateKPIVariant error:', error),
    });

    const deleteVariantSub = client.graphql({ query: onDeleteKPIVariant, ...(variables && { variables }) }).subscribe({
      next: ({ data }) => {
        if (isValidSubscriptionData(data, 'onDeleteKPIVariant')) {
          console.debug('[KPISubscription] onDeleteKPIVariant data received:', data);
          // Cache invalidation
          queryClientRef.current.invalidateQueries({ queryKey: kpiVariantQueryKeys.all });
          queryClientRef.current.invalidateQueries({ queryKey: rootKPIQueryKeys.all });
          // Notify listeners
          listenersRef.current.forEach(({ callbacks }) =>
            callbacks.onDeleteKPIVariant?.(data as OnDeleteKPIVariantSubscription),
          );
        }
      },
      error: (error) => console.error('[KPISubscription] onDeleteKPIVariant error:', error),
    });

    const updateRootKPISub = client.graphql({ query: onUpdateRootKPI }).subscribe({
      next: ({ data }) => {
        if (isValidSubscriptionData(data, 'onUpdateRootKPI')) {
          console.debug('[KPISubscription] onUpdateRootKPI data received:', data);
          // Cache invalidation
          queryClientRef.current.invalidateQueries({ queryKey: rootKPIQueryKeys.all });
          queryClientRef.current.invalidateQueries({ queryKey: kpiVariantQueryKeys.all });
          // Notify listeners
          listenersRef.current.forEach(({ callbacks }) =>
            callbacks.onUpdateRootKPI?.(data as OnUpdateRootKPISubscription),
          );
        }
      },
      error: (error) => console.error('[KPISubscription] onUpdateRootKPI error:', error),
    });

    return () => {
      updateVariantSub.unsubscribe();
      deleteVariantSub.unsubscribe();
      updateRootKPISub.unsubscribe();
    };
  }, [pageIds]);

  const contextValue = useMemo<KPISubscriptionContextType>(() => ({ registerListener }), [registerListener]);

  return <KPISubscriptionContext value={contextValue}>{children}</KPISubscriptionContext>;
};

/**
 * Custom hook to access the KPISubscription context
 */
export const useKPISubscriptionContext = (): KPISubscriptionContextType => {
  return use(KPISubscriptionContext);
};
